import { useContext, useEffect, useRef } from 'react';
import { ChromeTokens } from './tokens';
import { PanelCompanionContext } from './panelCompanion';

export interface PanelSurfaceProps {
  open: boolean;
  anchor?: 'center' | 'top-right' | 'sheet-bottom';
  onDismiss: () => void;
  ariaLabel: string;
  dismissOnOutsideClick?: boolean;
  children: React.ReactNode;
}

/*
 * A centred panel centres in the part of the screen the keyboard leaves and
 * scrolls within it (`--keyboard-h`, chrome/keyboardInset.ts, native only; 0 on
 * the web). A fixed panel is out of keyboardInset's reach: it does not move with
 * a document scroll, so without this the keyboard covers its lower half in iPad
 * landscape and nothing can bring those fields or its buttons up.
 */
const KEYBOARD_FREE = '(100% - var(--keyboard-h, 0px))';

const anchorStyles: Record<string, React.CSSProperties> = {
  center: {
    top: `calc(${KEYBOARD_FREE} / 2)`,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: `calc(${KEYBOARD_FREE} - 32px)`,
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  'top-right': { top: 64, right: 12, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' },
  'sheet-bottom': { bottom: 0, left: 0, right: 0, borderRadius: `${ChromeTokens.radius.lg}px ${ChromeTokens.radius.lg}px 0 0` },
};

export function PanelSurface({ open, anchor = 'center', onDismiss, ariaLabel, dismissOnOutsideClick = true, children }: PanelSurfaceProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const companion = useContext(PanelCompanionContext);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onDismiss]);

  /*
   * Focus moves into the dialog on open and back to what opened it on close
   * (13.11 accessibility pass, 2026-10-21), so VoiceOver and a keyboard start
   * inside the panel. A child that autofocuses itself keeps that focus.
   */
  //
  // Two frames after mount, not in the mounting commit: on build 1.0 (4)
  // VoiceOver reached every item but announced nothing on open — WKWebView's
  // VoiceOver does not follow focus onto a node that has not been laid out
  // yet. The target is the panel's first heading (made focusable), which
  // VoiceOver reads reliably, else the dialog with its aria-label.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        const panel = panelRef.current;
        if (!panel || panel.contains(document.activeElement)) return;
        const heading = panel.querySelector<HTMLElement>('h1, h2, h3');
        if (heading && !heading.hasAttribute('tabindex')) {
          heading.tabIndex = -1;
          // Only a landing place for the screen reader, never a Tab stop, so no ring: on a
          // panel that opens with the page the browser drew one round the title.
          heading.style.outline = 'none';
        }
        (heading ?? panel).focus({ preventScroll: true });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
      if (opener && opener !== document.body && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open]);

  /*
   * Focus never walks out into the page behind (a notice row's link, the bar):
   * whatever moves it there — Tab past the last control, a VoiceOver swipe —
   * it comes back to the panel. Another modal (the grown-up gate, a panel
   * opened on top) may take it. Guarding focusin rather than wrapping Tab by
   * hand: WebKit's Tab skips buttons, and the tab strips use roving tabindex,
   * so "the last control" is not knowable from the markup.
   */
  useEffect(() => {
    if (!open) return;
    const onFocusIn = (e: FocusEvent) => {
      const panel = panelRef.current;
      const target = e.target as Element | null;
      if (!panel || !target || panel.contains(target)) return;
      if (target.closest?.('[aria-modal="true"]')) return;
      panel.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open]);

  if (!open) return null;

  /*
   * Only the scrim is aria-hidden. It used to wrap the dialog, which hid every
   * panel (paywall, settings, parental controls, the grown-up gate inside them)
   * from VoiceOver and XCUITest: an aria-hidden ancestor removes its whole
   * subtree from the accessibility tree.
   */
  /*
   * With a companion (see panelCompanion.ts) the dialog is the whole layer —
   * companion, scrim and panel — so the companion is inside the modal; the
   * panel box is then a plain child. Without one, the panel box is the dialog.
   */
  const dialog = {
    ref: panelRef,
    role: 'dialog',
    'aria-label': ariaLabel,
    'aria-modal': true,
    tabIndex: -1,
  } as const;
  return (
    <div
      {...(companion ? dialog : {})}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, outline: 'none' }}
      onClick={dismissOnOutsideClick ? onDismiss : undefined}
    >
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(47,58,69,0.3)' }} />
      {companion && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
          {companion}
        </div>
      )}
      <div
        {...(companion ? {} : dialog)}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          ...anchorStyles[anchor],
          background: ChromeTokens.color.base,
          borderRadius: anchor === 'sheet-bottom' ? undefined : ChromeTokens.radius.lg,
          boxShadow: ChromeTokens.shadow.lifted,
          padding: ChromeTokens.spacing.xl,
          outline: 'none',
        }}
      >
        {/* A panel opened from inside this one does not draw the companion again. */}
        <PanelCompanionContext.Provider value={null}>{children}</PanelCompanionContext.Provider>
      </div>
    </div>
  );
}
